// Marketing analytics section (System 2) — embedded as a tab in the
// "Comptabilité" cockpit. True acquisition cost + return per channel and per
// campaign, reusing the accounting data model (order_costs contribution +
// isDelivered). Spend feeds in via /marketing/sync (API adapters) OR the
// manual / CSV bridge so it works today.
//
// Premium cream/gold: semantic brand tokens, SVG icons, Fraunces money,
// ROAS color-coded (sage good / terracotta bad), mobile-first, ≥44px touch
// targets, motion respects prefers-reduced-motion via .adm-rise.

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import {
  marketingApi,
  type MarketingChannelRow,
  type MarketingCampaignRow,
  type AdAccountsConfig,
  type AdChannel,
  type ManualSpendInput,
  type CsvSpendRow,
  type ConversionEventRecord,
  type ConversionResult,
} from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ADM_COLORS as COLORS, withAlpha } from '@/lib/brand';
import { useT } from '@/lib/i18n';
import { WhatsappFunnelCard } from './WhatsappFunnelCard';

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const cardStyle = {
  background: COLORS.bgCard,
  borderColor: COLORS.borderSubtle,
  boxShadow: '0 1px 3px rgba(46,36,24,0.04), 0 8px 24px rgba(46,36,24,0.05)',
} as const;

const mad = (n: number) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n))} DH`;
const numFmt = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
const fmtRatio = (n: number | null, suffix = '') => (n == null ? '—' : `${n.toFixed(2)}${suffix}`);
const fmtMoneyOrDash = (n: number | null) => (n == null ? '—' : mad(n));
const moneyColor = (n: number) =>
  n > 0 ? COLORS.success : n < 0 ? COLORS.error : COLORS.textSecondary;

// ROAS heuristic: ≥ 2 good (sage), 1–2 caution (amber), < 1 bad (terracotta).
const roasColor = (r: number | null) =>
  r == null ? COLORS.textTertiary : r >= 2 ? COLORS.success : r >= 1 ? COLORS.warning : COLORS.error;

const CHANNEL_LABELS: Record<string, string> = {
  facebook: 'Facebook / Meta',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  google: 'Google',
  whatsapp: 'WhatsApp',
  chatgpt: 'ChatGPT',
  snapchat: 'Snapchat',
  youtube: 'YouTube',
  direct: 'Direct',
  other: 'Autre',
};
const channelLabel = (c: string) => CHANNEL_LABELS[c] ?? c;

const PAID: AdChannel[] = ['facebook', 'tiktok', 'google'];

function Icon({ d, className = 'w-[18px] h-[18px]' }: { d: React.ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" className={className}>
      {d}
    </svg>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-7">
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: COLORS.accentHover }}>{label}</span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${withAlpha(COLORS.accentPrimary, '55')}, transparent)` }} />
    </div>
  );
}

function KpiCard({ label, value, sub, color = COLORS.textPrimary, delay = 0 }: {
  label: string; value: string; sub?: string; color?: string; delay?: number;
}) {
  return (
    <div className="rounded-xl p-4 border adm-rise" style={{ ...cardStyle, animationDelay: `${delay}ms` }}>
      <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>{label}</p>
      <p className="text-[24px] leading-none mb-1" style={{ color, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}>{value}</p>
      {sub && <p className="text-[11px] mt-1" style={{ color: COLORS.textTertiary }}>{sub}</p>}
    </div>
  );
}

// ─── Manual spend modal ───────────────────────────────────────────────────────
const fInput = 'w-full px-2.5 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white';
const fStyle = { borderColor: COLORS.borderSubtle } as const;

function ManualSpendModal({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [channel, setChannel] = useState<AdChannel>('facebook');
  const [campaign, setCampaign] = useState('');
  const [spend, setSpend] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [impressions, setImpressions] = useState('');
  const [clicks, setClicks] = useState('');

  const save = useMutation({
    mutationFn: () => {
      const payload: ManualSpendInput = {
        channel,
        campaign_name: campaign || undefined,
        spend: Number(spend),
        spend_date: date,
        impressions: impressions ? Number(impressions) : undefined,
        clicks: clicks ? Number(clicks) : undefined,
      };
      return marketingApi.addManualSpend(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing-analytics'] });
      qc.invalidateQueries({ queryKey: ['marketing-spend'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      onClose();
    },
  });
  const valid = Number(spend) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}>
      <div className="w-full max-w-[460px] rounded-2xl p-5 shadow-2xl adm-rise" style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{t('Ajouter une dépense (manuel)')}</h3>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}>✕</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: COLORS.textSecondary }}>{t('Plateforme')}</label>
            <div className="grid grid-cols-3 gap-2">
              {PAID.map((k) => (
                <button key={k} type="button" onClick={() => setChannel(k)}
                  className="py-2 rounded-lg text-xs font-semibold border min-h-[44px]"
                  style={channel === k
                    ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
                    : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }}>
                  {t(channelLabel(k))}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Campagne (= utm_campaign pour le rapprochement)')}</label>
            <input className={fInput} style={fStyle} value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder={t('Nom de campagne')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Dépense (MAD)')}</label>
              <input className={fInput} style={fStyle} type="number" min="0" step="0.01" value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Date')}</label>
              <input className={fInput} style={fStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Impressions')}</label>
              <input className={fInput} style={fStyle} type="number" min="0" value={impressions} onChange={(e) => setImpressions(e.target.value)} placeholder={t('Optionnel')} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Clics')}</label>
              <input className={fInput} style={fStyle} type="number" min="0" value={clicks} onChange={(e) => setClicks(e.target.value)} placeholder={t('Optionnel')} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: COLORS.borderSubtle }}>
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>{t('Ajouter')}</Button>
          </div>
          {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Manual lead count ────────────────────────────────────────────────────────
// Records a lead count for a channel + date. These ADD to the leads counted
// automatically from orders (mirrors how manual spend adds to synced spend).
function ManualLeadsModal({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [channel, setChannel] = useState<AdChannel>('facebook');
  const [leads, setLeads] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () => marketingApi.addManualLead({
      channel,
      leads: Number(leads),
      lead_date: date,
      note: note || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing-analytics'] });
      onClose();
    },
  });
  const valid = leads !== '' && Number.isFinite(Number(leads)) && Number(leads) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}>
      <div className="w-full max-w-[460px] rounded-2xl p-5 shadow-2xl adm-rise" style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-4">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{t('Ajouter des leads (manuel)')}</h3>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}>✕</button>
        </div>
        <div className="space-y-3">
          <p className="text-[11px] -mt-1" style={{ color: COLORS.textTertiary }}>
            {t('Ces leads s’ajoutent aux leads comptés automatiquement depuis les commandes.')}
          </p>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: COLORS.textSecondary }}>{t('Plateforme')}</label>
            <div className="grid grid-cols-3 gap-2">
              {PAID.map((k) => (
                <button key={k} type="button" onClick={() => setChannel(k)}
                  className="py-2 rounded-lg text-xs font-semibold border min-h-[44px]"
                  style={channel === k
                    ? { background: COLORS.accentSoft, borderColor: COLORS.accentPrimary, color: COLORS.accentHover }
                    : { background: COLORS.bgCard, borderColor: COLORS.borderSubtle, color: COLORS.textSecondary }}>
                  {t(channelLabel(k))}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Nombre de leads')}</label>
              <input className={fInput} style={fStyle} type="number" min="0" step="1" value={leads} onChange={(e) => setLeads(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Date')}</label>
              <input className={fInput} style={fStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{t('Note')}</label>
            <input className={fInput} style={fStyle} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Optionnel (ex: campagne)')} />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: COLORS.borderSubtle }}>
            <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>{t('Ajouter')}</Button>
          </div>
          {save.isError && <p className="text-xs" style={{ color: COLORS.error }}>{(save.error as Error).message}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── CSV / XLSX import ────────────────────────────────────────────────────────
// Accepts a real .csv or .xlsx/.xls file (or pasted CSV). Headers are matched by
// flexible aliases so a raw Meta/TikTok/Google export usually maps without edits.

// → YYYY-MM-DD. Handles Date objects (XLSX cellDates), ISO strings, DD/MM/YYYY,
// and Excel serial day numbers.
function toYmd(v: unknown): string {
  if (v == null || v === '') return '';
  if (v instanceof Date && !isNaN(v.getTime())) {
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${v.getFullYear()}-${m}-${d}`;
  }
  if (typeof v === 'number' && v > 59 && v < 60000) {
    // Excel serial date (days since 1899-12-30)
    const parsed = XLSX.SSF?.parse_date_code?.(v);
    if (parsed) {
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${parsed.y}-${m}-${d}`;
    }
  }
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  const dmy = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  return s;
}

function num(v: unknown): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[^\d.,-]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
  const n = Number(s);
  return isNaN(n) ? 0 : n;
}

// Find the first record key whose lowercased name contains any alias substring.
function findKey(keys: string[], aliases: string[]): string | undefined {
  const lk = keys.map((k) => ({ k, l: k.trim().toLowerCase() }));
  for (const a of aliases) {
    const hit = lk.find(({ l }) => l === a) || lk.find(({ l }) => l.includes(a));
    if (hit) return hit.k;
  }
  return undefined;
}

function mapRecords(records: Record<string, unknown>[]): CsvSpendRow[] {
  if (!records.length) return [];
  const keys = Object.keys(records[0]);
  const kChannel = findKey(keys, ['channel', 'canal', 'platform', 'plateforme', 'source', 'réseau', 'reseau']);
  const kCampaign = findKey(keys, ['campaign_name', 'campaign name', 'campaign', 'campagne', 'ad set', 'adset']);
  const kSpend = findKey(keys, ['amount spent', 'montant dépensé', 'montant depense', 'spend', 'cost', 'coût', 'cout', 'montant', 'dépense', 'depense']);
  const kDate = findKey(keys, ['spend_date', 'date', 'day', 'jour', 'reporting starts', 'report date']);
  const kCurrency = findKey(keys, ['currency', 'devise', 'monnaie', 'account_currency', 'currency code']);
  const kImp = findKey(keys, ['impressions', 'impr', 'affichages']);
  const kClk = findKey(keys, ['link clicks', 'clicks', 'clics', 'clic']);
  const rows: CsvSpendRow[] = [];
  for (const r of records) {
    // Channel stays RAW — the worker normalizes (Facebook/FB/Meta→facebook…) and
    // reports the actual offending value if it's genuinely unknown.
    const channel = kChannel ? String(r[kChannel] ?? '').trim() : '';
    const spend = kSpend ? num(r[kSpend]) : 0;
    const spend_date = kDate ? toYmd(r[kDate]) : '';
    if (!channel && !spend && !spend_date) continue; // skip blank rows
    rows.push({
      channel,
      campaign_name: kCampaign ? String(r[kCampaign] ?? '').trim() || undefined : undefined,
      spend,
      spend_date,
      currency: kCurrency ? String(r[kCurrency] ?? '').trim().toUpperCase() || undefined : undefined,
      impressions: kImp && r[kImp] !== '' && r[kImp] != null ? num(r[kImp]) : undefined,
      clicks: kClk && r[kClk] !== '' && r[kClk] != null ? num(r[kClk]) : undefined,
    });
  }
  return rows;
}

// Parse result carries diagnostics so the modal can self-explain a bad import
// (which delimiter was detected, which headers were found).
type ParseResult = { rows: CsvSpendRow[]; headers: string[]; delimiter: string };

// Excel (esp. French/Moroccan locale) saves CSV with ';' and prepends a UTF-8
// BOM. Detect the separator from the header line; strip the BOM.
function stripBom(s: string): string {
  return s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}
function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const candidates: Array<[string, string]> = [[',', ','], [';', ';'], ['\t', 'Tab']];
  let best = ','; let bestN = 0;
  for (const [d] of candidates) {
    const n = firstLine.split(d).length - 1;
    if (n > bestN) { bestN = n; best = d; }
  }
  return best;
}

function recordsToResult(records: Record<string, unknown>[], delimiter: string): ParseResult {
  const headers = records.length ? Object.keys(records[0]) : [];
  return { rows: mapRecords(records), headers, delimiter };
}

// CSV text → result (used by the paste fallback and .csv files).
function parseCsvText(text: string): ParseResult {
  const clean = stripBom(text).trim();
  if (!clean) return { rows: [], headers: [], delimiter: ',' };
  try {
    const FS = detectDelimiter(clean);
    // raw:true keeps cells as text so OUR num()/toYmd handle locale decimals
    // ("71,40", "1 234,56") and DD/MM/YYYY — SheetJS would otherwise mangle a
    // French decimal comma into thousands (71,40 → 7140).
    const wb = XLSX.read(clean, { type: 'string', raw: true, FS });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '', raw: true });
    return recordsToResult(records, FS === '\t' ? 'Tab' : FS);
  } catch {
    return { rows: [], headers: [], delimiter: ',' };
  }
}

// Any uploaded file → result. .xlsx/.xls parsed by SheetJS; everything else CSV.
async function parseFile(file: File): Promise<ParseResult> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const records = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
    return recordsToResult(records, 'xlsx');
  }
  const text = await file.text();
  return parseCsvText(text);
}

function CsvImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [fileRes, setFileRes] = useState<ParseResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [fileErr, setFileErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // Currency for rows that don't carry their own currency column. Meta exports
  // are USD, so default to USD; the worker converts everything to MAD.
  const [currency, setCurrency] = useState('USD');
  // Default channel for rows with no channel column — a raw Meta/TikTok export
  // has none (it's all one platform), so let the admin pick it for the file.
  const [channelDefault, setChannelDefault] = useState('facebook');

  const pastedRes = useMemo(() => parseCsvText(text), [text]);
  const res = fileRes ?? pastedRes;
  const parsed = res.rows;
  // No channel value on any row → file has no channel column (Meta/TikTok export);
  // the default-channel picker fills it.
  const channelMissing = parsed.length > 0 && !parsed.some((r) => r.channel);
  // Apply the picked defaults to any row missing channel / currency.
  const rowsToSend = useMemo(
    () => parsed.map((r) => ({ ...r, channel: r.channel || channelDefault, currency: r.currency || currency })),
    [parsed, channelDefault, currency],
  );
  const valid = useMemo(() => rowsToSend.filter((r) => r.channel && r.spend > 0 && r.spend_date), [rowsToSend]);

  const importM = useMutation({
    mutationFn: () => marketingApi.importCsv(rowsToSend),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['marketing-analytics'] });
      qc.invalidateQueries({ queryKey: ['marketing-spend'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
      // Only close when everything imported; otherwise keep the modal so the
      // per-row errors (with the offending value) stay visible.
      if (!data.errors?.length) onClose();
    },
  });

  async function ingest(f: File) {
    setFileErr('');
    setText('');
    setFileName(f.name);
    try {
      const r = await parseFile(f);
      setFileRes(r);
      if (!r.rows.length) setFileErr(t('Aucune ligne lisible. Vérifiez les en-têtes du fichier.'));
    } catch (e) {
      setFileRes(null);
      setFileErr((e as Error).message || t('Fichier illisible.'));
    }
  }
  function clearFile() {
    setFileRes(null);
    setFileName('');
    setFileErr('');
  }
  function downloadTemplate() {
    const csv = [
      'channel,campaign,spend,currency,date',
      'facebook,Mariage Juin,71.40,USD,2026-06-10',
      'tiktok,Omra 2026,300,MAD,2026-06-11',
      'google,Brand,12.5,USD,2026-06-11',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'modele_depenses_storiz.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}>
      <div className="w-full max-w-[560px] rounded-2xl p-5 shadow-2xl adm-rise" style={{ background: COLORS.bgCard }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-3">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: HEADING, color: COLORS.textPrimary }}>{t('Importer des dépenses (CSV / Excel)')}</h3>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            className="ml-auto w-8 h-8 rounded-lg border flex items-center justify-center"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textTertiary }}>✕</button>
        </div>
        <div className="flex items-start gap-2 mb-2.5 flex-wrap">
          <p className="text-[11px] flex-1 min-w-[200px]" style={{ color: COLORS.textTertiary }}>
            {t('Colonnes reconnues:')} <code>channel, campaign, spend, currency, date</code> {t('— channel ∈ facebook/meta, tiktok, google, organic. Pour les leads, utilisez « + Leads (manuel) ».')}
          </p>
          <button type="button" onClick={downloadTemplate}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border whitespace-nowrap"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.accentHover }}>
            ⬇ {t('Modèle CSV')}
          </button>
        </div>

        {/* Default channel + currency pickers — used for rows whose file has no
            such column (a raw Meta/TikTok export has no channel column). */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <label className="text-[11px] font-semibold" style={{ color: COLORS.textSecondary }}>{t('Canal par défaut')}</label>
          <select value={channelDefault} onChange={(e) => setChannelDefault(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textPrimary }}>
            <option value="facebook">Facebook / Meta</option>
            <option value="tiktok">TikTok</option>
            <option value="google">Google</option>
            <option value="organic">Organic</option>
          </select>
          <span className="text-[10px]" style={{ color: COLORS.textTertiary }}>{t('(si pas de colonne canal)')}</span>
        </div>
        <div className="flex items-center gap-2 mb-2.5 flex-wrap">
          <label className="text-[11px] font-semibold" style={{ color: COLORS.textSecondary }}>{t('Devise par défaut')}</label>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border text-xs font-semibold bg-white"
            style={{ borderColor: COLORS.borderSubtle, color: COLORS.textPrimary }}>
            <option value="USD">USD ($)</option>
            <option value="MAD">MAD (DH)</option>
            <option value="EUR">EUR (€)</option>
          </select>
          <span className="text-[10px]" style={{ color: COLORS.textTertiary }}>{t('(converti en MAD)')}</span>
        </div>

        {/* Dropzone */}
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) ingest(f); }}
          className="flex flex-col items-center justify-center gap-1.5 w-full rounded-xl border-2 border-dashed cursor-pointer py-6 px-4 text-center transition-colors"
          style={{
            borderColor: dragOver ? COLORS.accentPrimary : COLORS.borderSubtle,
            background: dragOver ? 'rgba(201,162,74,0.06)' : COLORS.bgPrimary,
          }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.accentPrimary} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span className="text-[12.5px] font-medium" style={{ color: COLORS.textPrimary }}>
            {t('Glissez un fichier ici ou cliquez pour choisir')}
          </span>
          <span className="text-[11px]" style={{ color: COLORS.textTertiary }}>{t('CSV, XLSX ou XLS')}</span>
          <input type="file" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) ingest(f); e.target.value = ''; }} />
        </label>

        {fileName && (
          <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-lg border text-[12px]"
            style={{ borderColor: COLORS.borderSubtle, background: COLORS.bgCard, color: COLORS.textPrimary }}>
            <span className="truncate">📄 {fileName}</span>
            <button type="button" onClick={clearFile} className="ml-auto text-[11px]" style={{ color: COLORS.textTertiary }}>{t('Retirer')}</button>
          </div>
        )}
        {fileErr && <p className="text-[11px] mt-1.5" style={{ color: COLORS.error }}>{fileErr}</p>}

        {/* Paste fallback (only when no file chosen) */}
        {!fileRes && (
          <>
            <p className="text-[11px] mt-3 mb-1" style={{ color: COLORS.textTertiary }}>{t('… ou collez du CSV:')}</p>
            <textarea className="w-full h-28 px-2.5 py-2 border rounded-lg text-xs font-mono bg-white" style={fStyle}
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="channel,campaign_name,spend,spend_date&#10;facebook,Mariage Juin,1200,2026-06-10" />
          </>
        )}

        {/* Self-diagnosing panel: detected delimiter + headers */}
        {res.headers.length > 0 && (
          <p className="text-[10px] mt-2 px-2 py-1 rounded-md" style={{ background: COLORS.bgPrimary, color: COLORS.textTertiary }}>
            {t('Délimiteur')}: <code>{res.delimiter}</code> · {t('En-têtes')}: <code>{res.headers.join(', ')}</code>
          </p>
        )}
        {channelMissing && (
          <p className="text-[11px] mt-1.5" style={{ color: COLORS.accentHover }}>
            {t('Pas de colonne « channel » — le canal par défaut ci-dessus sera appliqué à toutes les lignes.')}
          </p>
        )}

        <p className="text-[11px] mt-2" style={{ color: COLORS.textSecondary }}>
          {t('{n} ligne(s) détectée(s).', { n: parsed.length })}
          {parsed.length > valid.length && (
            <span style={{ color: COLORS.error }}> {t('{n} ignorée(s) (données incomplètes).', { n: parsed.length - valid.length })}</span>
          )}
        </p>

        {/* Preview */}
        {valid.length > 0 && (
          <div className="mt-1.5 max-h-32 overflow-auto rounded-lg border text-[11px]" style={{ borderColor: COLORS.borderSubtle }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: COLORS.bgPrimary, color: COLORS.textTertiary }}>
                  <th className="text-left px-2 py-1 font-medium">{t('Canal')}</th>
                  <th className="text-left px-2 py-1 font-medium">{t('Campagne')}</th>
                  <th className="text-right px-2 py-1 font-medium">{t('Dépense')}</th>
                  <th className="text-left px-2 py-1 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {valid.slice(0, 6).map((r, i) => (
                  <tr key={i} style={{ color: COLORS.textPrimary, borderTop: `1px solid ${COLORS.borderSubtle}` }}>
                    <td className="px-2 py-1">{r.channel}</td>
                    <td className="px-2 py-1 truncate max-w-[140px]">{r.campaign_name || '—'}</td>
                    <td className="px-2 py-1 text-right">{r.spend}</td>
                    <td className="px-2 py-1">{r.spend_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {valid.length > 6 && <p className="px-2 py-1 text-[10px]" style={{ color: COLORS.textTertiary }}>{t('+{n} de plus…', { n: valid.length - 6 })}</p>}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 mt-2 border-t" style={{ borderColor: COLORS.borderSubtle }}>
          <Button variant="ghost" size="sm" onClick={onClose}>{t('Annuler')}</Button>
          <Button size="sm" onClick={() => importM.mutate()} loading={importM.isPending} disabled={parsed.length === 0}>
            {t('Importer')} {parsed.length > 0 ? `(${parsed.length})` : ''}
          </Button>
        </div>
        {importM.isError && <p className="text-xs mt-2" style={{ color: COLORS.error }}>{(importM.error as Error).message}</p>}
        {importM.data && (
          <div className="mt-2">
            <p className="text-xs" style={{ color: importM.data.skipped ? COLORS.warning : COLORS.success }}>
              {t('{imported} importée(s), {skipped} ignorée(s).', { imported: importM.data.imported, skipped: importM.data.skipped })}
            </p>
            {importM.data.errors?.length > 0 && (
              <ul className="mt-1 max-h-24 overflow-auto text-[11px]" style={{ color: COLORS.error }}>
                {importM.data.errors.slice(0, 8).map((e, i) => <li key={i}>• {e}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Ad-accounts config panel ─────────────────────────────────────────────────
function AdAccountsPanel() {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['marketing-ad-accounts'], queryFn: () => marketingApi.getAdAccounts() });
  const [draft, setDraft] = useState<AdAccountsConfig | null>(null);
  const cfg = draft ?? q.data?.ad_accounts ?? {};
  const tokens = q.data?.token_configured;
  const pixels = q.data?.pixel_configured;

  const save = useMutation({
    mutationFn: () => marketingApi.putAdAccounts(cfg),
    onSuccess: () => { setDraft(null); qc.invalidateQueries({ queryKey: ['marketing-ad-accounts'] }); },
  });
  const set = (patch: AdAccountsConfig) => setDraft({ ...cfg, ...patch });

  // FX rates (currency → MAD). Saving recomputes every spend row into MAD.
  const [fxDraft, setFxDraft] = useState<Record<string, number> | null>(null);
  const fx = fxDraft ?? q.data?.fx_rates ?? {};
  const setFx = (cur: string, val: number) => setFxDraft({ ...fx, [cur]: val });
  const saveFx = useMutation({
    mutationFn: () => marketingApi.putFxRates(fx),
    onSuccess: () => {
      setFxDraft(null);
      qc.invalidateQueries({ queryKey: ['marketing-ad-accounts'] });
      qc.invalidateQueries({ queryKey: ['marketing-analytics'] });
      qc.invalidateQueries({ queryKey: ['marketing-spend'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
    },
  });

  // Conversion-side id field (System 3) rendered under each channel.
  const SubField = ({ label, value, onChange, hint }: {
    label: string; value: string; onChange: (v: string) => void; hint?: string;
  }) => (
    <div className="mt-2">
      <label className="block text-[11px] font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{label}</label>
      <input className={fInput} style={fStyle} value={value} onChange={(e) => onChange(e.target.value)} placeholder="—" />
      {hint && <p className="text-[10px] mt-1" style={{ color: COLORS.textTertiary }}>{hint}</p>}
    </div>
  );

  const Row = ({ ch, children, idLabel, idVal, onId, enabled, onEnabled }: {
    ch: AdChannel; children?: React.ReactNode; idLabel: string; idVal: string;
    onId: (v: string) => void; enabled: boolean; onEnabled: (v: boolean) => void;
  }) => (
    <div className="py-3 border-b last:border-b-0" style={{ borderColor: COLORS.borderSubtle }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{t(channelLabel(ch))}</span>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span className="text-[11px] flex items-center gap-1.5">
            <span style={{ color: tokens?.[ch] ? COLORS.success : COLORS.textTertiary }}>
              {tokens?.[ch] ? t('● Token') : t('○ Token (secret)')}
            </span>
            <span style={{ color: pixels?.[ch] ? COLORS.success : COLORS.textTertiary }}>
              {pixels?.[ch] ? t('● Pixel/ID') : t('○ Pixel/ID')}
            </span>
          </span>
          <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)}
            className="w-5 h-5 min-w-[20px]" style={{ accentColor: COLORS.accentPrimary }} />
          <span className="text-xs" style={{ color: COLORS.textSecondary }}>{t('Actif')}</span>
        </label>
      </div>
      <label className="block text-[11px] font-semibold mb-1" style={{ color: COLORS.textSecondary }}>{idLabel}</label>
      <input className={fInput} style={fStyle} value={idVal} onChange={(e) => onId(e.target.value)} placeholder="—" />
      {children}
    </div>
  );

  return (
    <div className="rounded-xl border p-5 adm-rise" style={cardStyle}>
      <p className="text-[11px] mb-3" style={{ color: COLORS.textTertiary }}>
        {t('Identifiants de compte (non secrets). Les')} <strong>{t('jetons API')}</strong> {t('se configurent en secret worker')}
        (META_ACCESS_TOKEN, TIKTOK_ACCESS_TOKEN, GOOGLE_ADS_DEVELOPER_TOKEN + GOOGLE_ADS_OAUTH_TOKEN).
      </p>
      {q.isLoading ? (
        <p className="text-sm" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>
      ) : (
        <>
          <Row ch="facebook" idLabel={t('Ad Account ID (act_…)')} idVal={cfg.facebook?.ad_account_id ?? ''}
            onId={(v) => set({ facebook: { ...cfg.facebook, ad_account_id: v } })}
            enabled={!!cfg.facebook?.enabled} onEnabled={(v) => set({ facebook: { ...cfg.facebook, enabled: v } })}>
            <SubField label={t('Pixel ID (Conversions API)')} value={cfg.facebook?.pixel_id ?? ''}
              onChange={(v) => set({ facebook: { ...cfg.facebook, pixel_id: v } })}
              hint={t('Requis pour envoyer les conversions confirmées à Meta.')} />
          </Row>
          <Row ch="tiktok" idLabel={t('Advertiser ID')} idVal={cfg.tiktok?.advertiser_id ?? ''}
            onId={(v) => set({ tiktok: { ...cfg.tiktok, advertiser_id: v } })}
            enabled={!!cfg.tiktok?.enabled} onEnabled={(v) => set({ tiktok: { ...cfg.tiktok, enabled: v } })}>
            <SubField label={t('Pixel ID / Event Set ID (Events API)')} value={cfg.tiktok?.pixel_id ?? ''}
              onChange={(v) => set({ tiktok: { ...cfg.tiktok, pixel_id: v } })}
              hint={t('Requis pour envoyer les conversions confirmées à TikTok.')} />
          </Row>
          <Row ch="google" idLabel={t('Customer ID')} idVal={cfg.google?.customer_id ?? ''}
            onId={(v) => set({ google: { ...cfg.google, customer_id: v } })}
            enabled={!!cfg.google?.enabled} onEnabled={(v) => set({ google: { ...cfg.google, enabled: v } })}>
            <label className="block text-[11px] font-semibold mt-2 mb-1" style={{ color: COLORS.textSecondary }}>{t('Login Customer ID (compte manager, optionnel)')}</label>
            <input className={fInput} style={fStyle} value={cfg.google?.login_customer_id ?? ''}
              onChange={(e) => set({ google: { ...cfg.google, login_customer_id: e.target.value } })} placeholder="—" />
            <SubField label={t('Conversion Action ID (conversions hors-ligne)')} value={cfg.google?.conversion_action_id ?? ''}
              onChange={(v) => set({ google: { ...cfg.google, conversion_action_id: v } })}
              hint={t('Requis pour téléverser les conversions à Google. Nécessite un gclid sur la commande.')} />
          </Row>
          <div className="flex justify-end pt-3">
            <Button size="sm" variant="secondary" loading={save.isPending} disabled={!draft} onClick={() => save.mutate()}>{t('Enregistrer')}</Button>
          </div>

          {/* FX rates → MAD */}
          <div className="pt-4 mt-1 border-t" style={{ borderColor: COLORS.borderSubtle }}>
            <p className="text-sm font-semibold mb-1" style={{ color: COLORS.textPrimary }}>{t('Taux de change → MAD')}</p>
            <p className="text-[11px] mb-2.5" style={{ color: COLORS.textTertiary }}>
              {t('Meta facture en USD. Tous les montants sont convertis en MAD avec ce taux.')}
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-[11px] font-semibold mb-1" style={{ color: COLORS.textSecondary }}>USD → MAD</label>
                <input type="number" step="0.01" min="0"
                  className="px-2.5 py-2 border rounded-lg text-sm bg-white w-[110px]" style={fStyle}
                  value={fx.USD ?? ''} onChange={(e) => setFx('USD', Number(e.target.value))} placeholder="10" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold mb-1" style={{ color: COLORS.textSecondary }}>EUR → MAD</label>
                <input type="number" step="0.01" min="0"
                  className="px-2.5 py-2 border rounded-lg text-sm bg-white w-[110px]" style={fStyle}
                  value={fx.EUR ?? ''} onChange={(e) => setFx('EUR', Number(e.target.value))} placeholder="11" />
              </div>
              <Button size="sm" variant="secondary" loading={saveFx.isPending} disabled={!fxDraft} onClick={() => saveFx.mutate()}>
                {t('Enregistrer les taux')}
              </Button>
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: COLORS.textTertiary }}>{t('Enregistrer recalcule toutes les dépenses en MAD.')}</p>
            {saveFx.isError && <p className="text-xs mt-1" style={{ color: COLORS.error }}>{(saveFx.error as Error).message}</p>}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tables ───────────────────────────────────────────────────────────────────
const TH = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
  <th className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider ${right ? 'text-right' : 'text-left'}`}
    style={{ color: COLORS.textTertiary }}>{children}</th>
);
const TD = ({ children, right, color }: { children: React.ReactNode; right?: boolean; color?: string }) => (
  <td className={`px-3 py-2.5 text-sm ${right ? 'text-right tabular-nums' : ''}`} style={{ color: color ?? COLORS.textPrimary }}>{children}</td>
);

type SortKey = 'spend' | 'roas' | 'cac' | 'confirmed' | 'contributionAfterAd';

function MetricTable({ rows, label, kind }: {
  rows: (MarketingChannelRow | MarketingCampaignRow)[];
  label: string;
  kind: 'channel' | 'campaign';
}) {
  const { t } = useT();
  const [sort, setSort] = useState<SortKey>('spend');
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = (a[sort] ?? -Infinity) as number;
      const bv = (b[sort] ?? -Infinity) as number;
      return bv - av;
    });
  }, [rows, sort]);

  const Header = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-right cursor-pointer select-none"
      style={{ color: sort === k ? COLORS.accentHover : COLORS.textTertiary }} onClick={() => setSort(k)}>
      {children}{sort === k ? ' ↓' : ''}
    </th>
  );

  return (
    <div className="rounded-xl border overflow-hidden adm-rise" style={cardStyle}>
      <div className="px-5 py-4 border-b" style={{ borderColor: COLORS.borderSubtle }}>
        <h3 className="text-lg" style={{ fontFamily: HEADING, color: COLORS.textPrimary, fontWeight: 500 }}>{label}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b" style={{ borderColor: COLORS.borderSubtle, background: COLORS.bgElevated }}>
              <TH>{kind === 'channel' ? t('Canal') : t('Campagne')}</TH>
              <Header k="spend">{t('Dépense')}</Header>
              <TH right>{t('Leads')}</TH>
              <Header k="confirmed">{t('Confirmés')}</Header>
              <TH right>CPL</TH>
              <Header k="cac">CAC</Header>
              <TH right>AOV</TH>
              <Header k="roas">ROAS</Header>
              <Header k="contributionAfterAd">{t('Contrib. après pub')}</Header>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-sm" style={{ color: COLORS.textTertiary }}>{t('Aucune donnée sur cette période.')}</td></tr>
            )}
            {sorted.map((r, i) => {
              const name = kind === 'channel' ? t(channelLabel((r as MarketingChannelRow).channel)) : (r as MarketingCampaignRow).campaign;
              const chan = kind === 'campaign' ? (r as MarketingCampaignRow).channel : null;
              return (
                <tr key={`${name}-${i}`} className="border-b last:border-b-0" style={{ borderColor: COLORS.borderSubtle }}>
                  <TD>
                    <span className="font-semibold">{name}</span>
                    {chan && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: COLORS.bgElevated, color: COLORS.textSecondary }}>{t(channelLabel(chan))}</span>}
                  </TD>
                  <TD right>{mad(r.spend)}</TD>
                  <TD right>{numFmt(r.leads)}</TD>
                  <TD right>{numFmt(r.confirmed)}</TD>
                  <TD right color={COLORS.textSecondary}>{fmtMoneyOrDash(r.cpl)}</TD>
                  <TD right>{fmtMoneyOrDash(r.cac)}</TD>
                  <TD right color={COLORS.textSecondary}>{fmtMoneyOrDash(r.aov)}</TD>
                  <TD right color={roasColor(r.roas)}>{fmtRatio(r.roas, '×')}</TD>
                  <TD right color={moneyColor(r.contributionAfterAd)}>{mad(r.contributionAfterAd)}</TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Conversions sent audit panel (System 3) ──────────────────────────────────
const EVENT_LABELS: Record<string, string> = { Purchase: 'Achat confirmé', Delivered: 'Livré' };
const MATCH_LABELS: Record<string, string> = {
  fbclid: 'fbclid', ttclid: 'ttclid', gclid: 'gclid', phone_hash: 'téléphone (hashé)', none: '—',
};
const RESULT_META: Record<ConversionResult, { label: string; color: string; dot: string }> = {
  sent:    { label: 'Envoyé',  color: COLORS.success,     dot: COLORS.success },
  skipped: { label: 'Ignoré',  color: COLORS.textTertiary, dot: COLORS.textTertiary },
  error:   { label: 'Erreur',  color: COLORS.error,       dot: COLORS.error },
};

function StatusPill({ result }: { result: ConversionResult }) {
  const { t } = useT();
  const m = RESULT_META[result];
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ color: m.color, background: `${m.dot}1A` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} aria-hidden />
      {t(m.label)}
    </span>
  );
}

function ConversionsPanel({ dates }: { dates: { from?: string; to?: string } }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [chan, setChan] = useState<AdChannel | ''>('');
  const [status, setStatus] = useState<ConversionResult | ''>('');

  const q = useQuery({
    queryKey: ['marketing-conversions', dates.from, dates.to, chan, status],
    queryFn: () => marketingApi.listConversions({
      from: dates.from, to: dates.to,
      channel: chan || undefined,
      status: status || undefined,
    }),
  });

  const retry = useMutation({
    mutationFn: (id: string) => marketingApi.retryConversion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing-conversions'] }),
  });

  const rows = q.data?.conversions ?? [];
  const dt = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const FilterBtn = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick}
      className="px-3 py-1.5 rounded-lg text-xs font-semibold border min-h-[36px] transition-colors"
      style={{
        borderColor: active ? COLORS.accentPrimary : COLORS.borderSubtle,
        background: active ? `${withAlpha(COLORS.accentPrimary, '1A')}` : COLORS.bgCard,
        color: active ? COLORS.accentHover : COLORS.textSecondary,
      }}>
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border overflow-hidden adm-rise" style={cardStyle}>
      <div className="px-5 py-4 border-b" style={{ borderColor: COLORS.borderSubtle }}>
        <h3 className="text-lg" style={{ fontFamily: HEADING, color: COLORS.textPrimary, fontWeight: 500 }}>{t('Conversions envoyées')}</h3>
        <p className="text-[11px] mt-0.5" style={{ color: COLORS.textTertiary }}>
          {t('Signal serveur (Meta CAPI · TikTok Events · Google offline) à la confirmation de la commande payante.')}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <FilterBtn active={chan === ''} onClick={() => setChan('')}>{t('Tous canaux')}</FilterBtn>
          {PAID.map((p) => <FilterBtn key={p} active={chan === p} onClick={() => setChan(p)}>{t(channelLabel(p))}</FilterBtn>)}
          <span className="w-px self-stretch mx-1" style={{ background: COLORS.borderSubtle }} />
          {(['sent', 'error', 'skipped'] as ConversionResult[]).map((s) => (
            <FilterBtn key={s} active={status === s} onClick={() => setStatus(status === s ? '' : s)}>{t(RESULT_META[s].label)}</FilterBtn>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-sm px-5 py-8 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>
      ) : q.isError ? (
        <p className="text-sm px-5 py-8 text-center" style={{ color: COLORS.error }}>{(q.error as Error).message}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b" style={{ borderColor: COLORS.borderSubtle, background: COLORS.bgElevated }}>
                <TH>{t('Commande')}</TH>
                <TH>{t('Client')}</TH>
                <TH>{t('Canal')}</TH>
                <TH>{t('Événement')}</TH>
                <TH right>{t('Valeur')}</TH>
                <TH>{t('Clé')}</TH>
                <TH>{t('Statut')}</TH>
                <TH>{t('Date')}</TH>
                <TH right>{t('Action')}</TH>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-sm" style={{ color: COLORS.textTertiary }}>{t('Aucune conversion sur cette période.')}</td></tr>
              )}
              {rows.map((r: ConversionEventRecord) => (
                <tr key={r.id} className="border-b last:border-b-0" style={{ borderColor: COLORS.borderSubtle }}>
                  <TD><span className="font-semibold">{r.order_name ?? '—'}</span></TD>
                  <TD color={COLORS.textSecondary}>{r.customer_name ?? '—'}</TD>
                  <TD>{t(channelLabel(r.channel))}</TD>
                  <TD color={COLORS.textSecondary}>{EVENT_LABELS[r.event_type] ? t(EVENT_LABELS[r.event_type]) : r.event_type}</TD>
                  <TD right>{r.value != null ? mad(r.value) : '—'}</TD>
                  <TD color={COLORS.textSecondary}>{MATCH_LABELS[r.match_key ?? 'none'] ? t(MATCH_LABELS[r.match_key ?? 'none']) : r.match_key}</TD>
                  <TD>
                    <StatusPill result={r.result} />
                    {r.result === 'error' && r.error_message && (
                      <span className="block text-[10px] mt-0.5 max-w-[200px] truncate" style={{ color: COLORS.error }} title={r.error_message}>{r.error_message}</span>
                    )}
                  </TD>
                  <TD color={COLORS.textTertiary}>{dt(r.created_at)}</TD>
                  <TD right>
                    {r.result === 'error' ? (
                      <button onClick={() => retry.mutate(r.id)} disabled={retry.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border min-h-[36px]"
                        style={{ borderColor: COLORS.borderSubtle, color: COLORS.accentHover, background: COLORS.bgCard }}>
                        <Icon d={<><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></>} className="w-3.5 h-3.5" />
                        {t('Réessayer')}
                      </button>
                    ) : <span style={{ color: COLORS.textTertiary }}>—</span>}
                  </TD>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────
/** Which outcome the conversion-rate card measures. The lead denominator is
 *  identical for all three — only the numerator changes. */
type ConvBasis = 'delivered' | 'confirmed' | 'paid';

export function MarketingSection({ dates }: { dates: { from?: string; to?: string } }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [showManual, setShowManual] = useState(false);
  const [showLeads, setShowLeads] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  // Which outcome the conversion card measures. Deliberately NOT persisted —
  // it is an exploratory toggle, and defaulting to "livrés" keeps the number
  // people quote to each other stable.
  const [convBasis, setConvBasis] = useState<ConvBasis>('delivered');

  const analyticsQ = useQuery({
    queryKey: ['marketing-analytics', dates.from, dates.to],
    queryFn: () => marketingApi.analytics(dates),
  });

  const sync = useMutation({
    mutationFn: () => marketingApi.syncNow(dates),
    onSuccess: (res) => {
      const parts = Object.entries(res.channels).map(([ch, r]) =>
        r.ok ? `${t(channelLabel(ch))}: ${r.rows_synced}` : `${t(channelLabel(ch))}: ${r.error ?? t('erreur')}`);
      setSyncMsg(parts.join(' · '));
      qc.invalidateQueries({ queryKey: ['marketing-analytics'] });
      qc.invalidateQueries({ queryKey: ['marketing-spend'] });
      qc.invalidateQueries({ queryKey: ['accounting-summary'] });
    },
    onError: (e) => setSyncMsg((e as Error).message),
  });

  const a = analyticsQ.data?.analytics;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Button size="sm" variant="secondary" loading={sync.isPending} onClick={() => sync.mutate()}>
          <Icon d={<><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></>} className="w-4 h-4 mr-1" />
          {t('Synchroniser les dépenses')}
        </Button>
        <Button size="sm" onClick={() => setShowManual(true)}>{t('+ Dépense (manuel)')}</Button>
        <Button size="sm" onClick={() => setShowLeads(true)}>{t('+ Leads (manuel)')}</Button>
        <Button size="sm" variant="ghost" onClick={() => setShowCsv(true)}>{t('Importer CSV')}</Button>
      </div>
      {syncMsg && <p className="text-[11px] mb-2" style={{ color: COLORS.textSecondary }}>{syncMsg}</p>}

      {analyticsQ.isLoading && <p className="text-sm py-10 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>}
      {analyticsQ.isError && <p className="text-sm py-10 text-center" style={{ color: COLORS.error }}>{(analyticsQ.error as Error).message}</p>}

      {a && (
        <>
          <SectionHeading label={t('Acquisition — vue d’ensemble')} />
          {/* Owner, 2026-10-10: the funnel, not one %. The old single rate stays one click away. */}
          <WhatsappFunnelCard from={dates.from} to={dates.to} />
          {a.funnel && a.funnel.realLeads > 0 && (
          <details className="mb-3">
            <summary className="text-[11px] cursor-pointer mb-2" style={{ color: COLORS.textTertiary }}>
              {t('Ancien calcul — un seul taux (commandes du site + WhatsApp)')}
            </summary>
          {(() => {
            // The DENOMINATOR never changes — the same real-lead cohort backs all
            // three options, so the percentages are directly comparable. Only the
            // measured outcome moves. Resets to "livrés" on every load: this is an
            // exploratory toggle, not a saved preference.
            const f = a.funnel!;
            const OUTCOME = {
              delivered: {
                n: f.delivered,
                short: t('album livré'),
                caption: t('leads réels (hors commandes test) ayant importé des photos ou venant des pubs → album livré'),
              },
              confirmed: {
                n: f.confirmed,
                short: t('commande confirmée'),
                caption: t('mêmes leads → commande confirmée par un agent (clic sur « ✓ Confirmer la commande »), pas un simple changement de statut'),
              },
              paid: {
                n: f.paid,
                short: t('commande payée'),
                caption: t('mêmes leads → commande arrivée au statut « payée ». Mesure une étape du pipeline, pas une preuve d’encaissement.'),
              },
            } as const;
            const sel = OUTCOME[convBasis];
            const rate = f.realLeads > 0 ? sel.n / f.realLeads : null;
            // The confirm button shipped mid-history; a window that reaches back
            // before it cannot show confirmations that never existed.
            const confirmGap =
              convBasis === 'confirmed' && f.confirmedSince && dates.from && dates.from < f.confirmedSince;

            return (
            <div className="rounded-xl p-4 border adm-rise mb-3" style={cardStyle} title={sel.caption}>
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{t('Taux de conversion')}</p>
                <select
                  value={convBasis}
                  onChange={(e) => setConvBasis(e.target.value as ConvBasis)}
                  aria-label={t('Mesurer la conversion vers')}
                  className="text-[11px] font-semibold rounded-lg px-2 py-1 border bg-white cursor-pointer focus:outline-none focus:ring-2"
                  style={{ borderColor: 'rgba(201,162,74,0.30)', color: COLORS.textSecondary }}
                >
                  <option value="delivered">{t('Albums livrés')}</option>
                  <option value="confirmed">{t('Commandes confirmées (bouton agent)')}</option>
                  <option value="paid">{t('Commandes payées')}</option>
                </select>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <p className="text-[32px] leading-none" style={{ color: COLORS.success, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}>
                  {rate == null ? '—' : `${Math.round(rate * 100)}%`}
                </p>
                {rate != null && (
                  <p className="text-[12px]" style={{ color: COLORS.textSecondary }}>
                    {t('≈ {n} sur 10 leads → {outcome}', { n: Math.round(rate * 10), outcome: sel.short })}
                  </p>
                )}
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>
                {sel.n} / {f.realLeads}
                {convBasis === 'delivered' && f.paidInTransit > 0 && (
                  <span style={{ color: COLORS.warning }}> · +{f.paidInTransit} {t('en route')}</span>
                )}
              </p>
              <p className="text-[10px] mt-1.5" style={{ color: COLORS.textTertiary }}>
                {sel.caption}
              </p>
              {confirmGap && (
                <p className="text-[10px] mt-1" style={{ color: COLORS.warning }}>
                  {t('⚠ Le bouton de confirmation existe depuis le {d} — les leads antérieurs ne peuvent pas être comptés.', { d: f.confirmedSince! })}
                </p>
              )}
            </div>
            );
          })()}
          </details>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard label={t('Dépense pub')} value={mad(a.totals.spend)} sub={t('{n} leads', { n: a.totals.leads })} delay={0} />
            <KpiCard label={t('CAC réel')} value={fmtMoneyOrDash(a.totals.cac)} sub={t('{n} confirmés', { n: a.totals.confirmed })} delay={60} />
            <KpiCard label="CPL" value={fmtMoneyOrDash(a.totals.cpl)} sub={t('par lead')} delay={120} />
            <KpiCard label="ROAS" value={fmtRatio(a.totals.roas, '×')} color={roasColor(a.totals.roas)} sub={t('revenu / dépense')} delay={180} />
            <KpiCard label={t('Contrib. après pub')} value={mad(a.totals.contributionAfterAd)} color={moneyColor(a.totals.contributionAfterAd)} sub={`AOV ${fmtMoneyOrDash(a.totals.aov)}`} delay={240} />
          </div>

          <SectionHeading label={t('Par canal')} />
          <MetricTable rows={a.channelRows} label={t('Canaux')} kind="channel" />

          <SectionHeading label={t('Par campagne')} />
          <MetricTable rows={a.campaignRows} label={t('Campagnes (triable)')} kind="campaign" />

          <SectionHeading label={t('Conversions envoyées')} />
          <ConversionsPanel dates={dates} />

          <SectionHeading label={t('Comptes publicitaires')} />
          <AdAccountsPanel />
        </>
      )}

      {showManual && <ManualSpendModal onClose={() => setShowManual(false)} />}
      {showLeads && <ManualLeadsModal onClose={() => setShowLeads(false)} />}
      {showCsv && <CsvImportModal onClose={() => setShowCsv(false)} />}
    </div>
  );
}
